import { resolveFlagCountryCode } from '../utils/currencyFlag.util';
import type { CompanyAccountCurrencyGroup, CompanyAccountOption } from '../types/companyAccountsSelect.type';
import type {
  CompanyAccountCatalogGroupResponse,
  CompanyAccountCatalogItemResponse,
  CompanyAccountsCatalogResponse,
} from '../types/companyAccountsCatalog.type';

/**
 * Traduce una cuenta del catálogo a la opción que consume el selector.
 * Devuelve **solo** los cuatro campos que la UI usa: el resto del item se
 * queda afuera, y no se usa spread para que un campo nuevo del backend no
 * llegue a la interfaz sin que nadie lo decida.
 */
function toCompanyAccountOption(account: CompanyAccountCatalogItemResponse): CompanyAccountOption {
  return {
    id: account.id,
    // El contrato permite `name` y `accountNumber` nulos (una cuenta sin
    // alias todavía). La UI nunca recibe null: el copy de la fila sin nombre
    // lo decide el componente, no el adapter.
    name: account.name ?? '',
    accountNumber: account.accountNumber ?? '',
    isMain: !account.isAdditional,
  };
}

function toCompanyAccountCurrencyGroup(group: CompanyAccountCatalogGroupResponse): CompanyAccountCurrencyGroup {
  return {
    currency: group.currencyCode,
    flagCountryCode: resolveFlagCountryCode(group.currencyCode),
    // Se conserva el orden que manda el backend: el record documenta
    // "main account followed by the additional accounts".
    accounts: (group.accounts ?? []).map(toCompanyAccountOption),
  };
}

/**
 * ML-65 — adapta el catálogo de cuentas de la empresa al tipo que ya consume
 * el selector (`CompanyAccountsSelectList`), para que la UI no dependa del
 * contrato del backend.
 * @param response - Respuesta cruda de `GET .../grouped-by-currency`.
 * @returns Los grupos de moneda listos para el selector.
 */
export function getCompanyAccountsCatalogAdapter(
  response: CompanyAccountsCatalogResponse
): CompanyAccountCurrencyGroup[] {
  return (response.currencies ?? []).map(toCompanyAccountCurrencyGroup);
}

/**
 * Ids de todas las cuentas del catálogo, principales y adicionales: el backend ya filtra
 * `grouped-by-currency` y solo devuelve las que el usuario puede operar.
 * @param response - Respuesta cruda de `GET .../grouped-by-currency`.
 */
export function getAssignedAccountIdsAdapter(response: CompanyAccountsCatalogResponse): Set<number> {
  const assignedIds = new Set<number>();

  for (const group of response.currencies ?? []) {
    for (const account of group.accounts ?? []) {
      assignedIds.add(account.id);
    }
  }

  return assignedIds;
}

/**
 * `true` si el catálogo trae alguna cuenta principal (`isAdditional === false`): como el backend
 * ya filtra por usuario, que venga es que el usuario la opera. Un `isAdditional` ausente no cuenta.
 * @param response - Respuesta cruda de `GET .../grouped-by-currency`.
 */
export function hasAssignedMainAccountAdapter(response: CompanyAccountsCatalogResponse): boolean {
  return (response.currencies ?? []).some((group) =>
    (group.accounts ?? []).some((account) => account.isAdditional === false)
  );
}
